import { expandEvents, isEventVisibleTo } from '../calendar/index.js';
import { upcomingBirthdaysInTz } from './birthdays.js';
import { DEFAULT_HOUSEHOLD_TZ, dateKeyInTz, shiftedStartOfDayInTz } from './tz.js';
/** New-items window: a rolling 24h, not a calendar day (ROADMAP §2 "added in the last 24h"). */
const NEW_ITEMS_WINDOW_MS = 24 * 60 * 60 * 1000;
function plural(n, word) {
    return `${n} ${word}${n === 1 ? '' : 's'}`;
}
function buildSummary(eventsCount, tasksCount, newItemsCount) {
    const eventsPhrase = eventsCount === 0 ? 'nothing on the calendar' : plural(eventsCount, 'event');
    const parts = [`Today: ${eventsPhrase}`];
    if (tasksCount > 0)
        parts.push(`${plural(tasksCount, 'task')} for you`);
    if (newItemsCount > 0)
        parts.push(`${plural(newItemsCount, 'new item')}`);
    return parts.join(' · ');
}
export function composeDigest(input) {
    const { docs, forUserId, now } = input;
    const tz = input.tz ?? DEFAULT_HOUSEHOLD_TZ;
    const todayStart = shiftedStartOfDayInTz(now, 0, tz);
    const todayEnd = shiftedStartOfDayInTz(now, 1, tz);
    const yesterdayStart = shiftedStartOfDayInTz(now, -1, tz);
    // --- Today's events, every member's (the day-at-a-glance), shared occurrence expansion.
    //
    // WS-15: the recipient's own "Just me" events (their `calendar:user:<id>` doc) belong in
    // their digest, and subset-shared events they are not part of do not. Both are applied
    // here so every digest caller — client view and server scheduler — filters identically.
    const visibleEvents = [
        ...docs.events.filter((event) => isEventVisibleTo(event, forUserId)),
        ...(docs.personalEventsByUserId?.[forUserId] ?? []),
    ];
    const occurrences = expandEvents(visibleEvents, {
        from: todayStart.getTime(),
        to: todayEnd.getTime(),
    });
    const todayEvents = occurrences.map((occurrence) => ({
        key: occurrence.key,
        eventId: occurrence.event.id,
        title: occurrence.event.title,
        start: occurrence.start,
        end: occurrence.end,
        allDay: occurrence.event.allDay,
        ownerIds: occurrence.event.ownerIds,
        location: occurrence.event.location,
    }));
    // --- Lists: open items assigned to you, and anything added in the last 24h.
    const listById = new Map(docs.lists.map((list) => [list.id, list]));
    const newItemsCutoff = now.getTime() - NEW_ITEMS_WINDOW_MS;
    const tasksForYou = [];
    const newItems = [];
    for (const list of docs.lists) {
        if (list.archived)
            continue;
        const items = docs.listItemsByListId[list.id] ?? [];
        for (const item of items) {
            if (item.header)
                continue;
            if (!item.checked && item.assigneeIds.includes(forUserId)) {
                tasksForYou.push({
                    listId: list.id,
                    listName: list.name,
                    listEmoji: list.emoji,
                    itemId: item.id,
                    text: item.text,
                    detail: item.detail,
                });
            }
            // Strictly-after the cutoff: an item created exactly 24h ago has aged out (see the
            // composer test for the boundary this buys — "last 24h" reads as a half-open window).
            if (Date.parse(item.createdAt) > newItemsCutoff) {
                newItems.push({
                    listId: list.id,
                    listName: list.name,
                    listEmoji: list.emoji,
                    itemId: item.id,
                    text: item.text,
                    addedBy: item.createdBy,
                    addedAt: item.createdAt,
                });
            }
        }
    }
    tasksForYou.sort((a, b) => listById.get(a.listId)?.name.localeCompare(listById.get(b.listId)?.name ?? '') ?? 0);
    newItems.sort((a, b) => b.addedAt.localeCompare(a.addedAt));
    // --- Check-ins: yesterday's posts plus anything overnight (yesterday-local-midnight → now).
    const checkinItems = docs.checkins
        .filter((checkin) => {
        const at = Date.parse(checkin.createdAt);
        return at >= yesterdayStart.getTime() && at <= now.getTime();
    })
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .map((checkin) => ({
        id: checkin.id,
        authorId: checkin.authorId,
        createdAt: checkin.createdAt,
        text: checkin.text,
        placeName: checkin.place?.name,
        hasPhoto: Boolean(checkin.photoRef),
    }));
    // --- Birthdays within a week (ROADMAP §2 "upcoming birthdays ≤7 days"). Timezone-safe
    // (see `birthdays.ts`) rather than calendar's runtime-local `upcomingBirthdays`.
    const upcoming = upcomingBirthdaysInTz(docs.birthdays, now, tz, 7);
    const birthdayItems = upcoming.map((entry) => ({
        id: entry.birthday.id,
        name: entry.birthday.name,
        date: entry.date,
        daysUntil: entry.daysUntil,
        turningAge: entry.turningAge,
        memberId: entry.birthday.memberId,
    }));
    return {
        forUserId,
        generatedAt: now.toISOString(),
        tz,
        todayKey: dateKeyInTz(now, tz),
        today: { events: todayEvents },
        tasks: { forYou: tasksForYou },
        newListItems: { items: newItems },
        checkins: { items: checkinItems },
        birthdays: { items: birthdayItems },
        summary: buildSummary(todayEvents.length, tasksForYou.length, newItems.length),
    };
}
//# sourceMappingURL=composer.js.map